import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CssBaseline,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  DashboardOutlined as DashboardIcon,
  LogoutOutlined as LogoutIcon,
  Inventory2Outlined as Inventory2Icon,
  CategoryOutlined as CategoryIcon,
  StorefrontOutlined as StorefrontIcon,
  GroupOutlined as PeopleIcon,
  ShoppingCartOutlined as ShoppingCartIcon,
  BrandingWatermarkOutlined as BrandingWatermarkIcon,
  ManageAccountsOutlined as ManageAccountsIcon,
  ExpandLess,
  ExpandMore,
  ListAltOutlined as ListAltIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  CheckCircleOutlined as ApprovalIcon, // ✅ Added Approval Icon
} from '@mui/icons-material';

import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import { Link, useLocation } from 'react-router-dom';
import Header from './Header';
import logo from '../assets/logo.svg';
import Color from './Color';

const fullDrawerWidth = 240;
const collapsedDrawerWidth = 72;

const Sidebar = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [openSubMenus, setOpenSubMenus] = useState({});

  const location = useLocation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    if (isTablet) setDesktopOpen(false);
    else if (isDesktop) setDesktopOpen(true);
  }, [isTablet, isDesktop]);

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem('settings')) || {};
    if (settings.company_logo) {
      const logoURL = settings.company_logo.startsWith('http')
        ? settings.company_logo
        : `${process.env.REACT_APP_API_BASE_URL || ''}${settings.company_logo}`;
      setCompanyLogo(logoURL);
    }
  }, []);

  // Initialize openSubMenus based on active path
  useEffect(() => {
    const initialSubMenus = {};
    mainMenuItems.forEach((item) => {
      if (item.children) {
        const isChildActive = item.children.some((child) =>
          location.pathname.startsWith(child.path)
        );
        initialSubMenus[item.text] = isChildActive;
      }
    });
    setOpenSubMenus(initialSubMenus);
  }, [location.pathname]);

  const currentDrawerWidth = desktopOpen ? fullDrawerWidth : collapsedDrawerWidth;
  const handleMobileToggle = () => setMobileOpen(!mobileOpen);
  const handleDesktopToggle = () => setDesktopOpen(!desktopOpen);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const userRole = parseInt(localStorage.getItem('role'));

  const mainMenuItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Branch', path: '/branches', icon: <StorefrontIcon />, roles: [1] },
    { text: 'Brand', path: '/brands', icon: <BrandingWatermarkIcon /> },
    {
      text: 'Categories',
      path: '/categories',
      icon: <CategoryIcon />,
      children: [
        { text: 'Sub Categories', path: '/sub-categories', icon: <ListAltIcon /> },
      ],
      hover: true,
    },
    { text: 'Products', path: '/products', icon: <Inventory2Icon /> },
    { text: 'Customer', path: '/customer', icon: <PeopleIcon /> },
    {
      text: 'Orders',
      icon: <ShoppingCartIcon />,
      children: [
        { text: 'Sweet Orders', path: '/orders', icon: <ShoppingBagIcon /> },
        { text: 'Cake Orders', path: '/cake-orders', icon: <CakeOutlinedIcon /> },
      ],
    },
    { text: 'Reports', path: '/reports', icon: <ListAltIcon /> },
    { text: 'User', path: '/users', icon: <ManageAccountsIcon />, roles: [1] },
     { text: 'Approval', path: '/approval', icon: <ApprovalIcon />, roles: [1] },
  ];

  const toggleSubMenu = (key, open) => {
    // Check if any child path is active
    const menuItem = mainMenuItems.find((item) => item.text === key);
    const isChildActive = menuItem?.children?.some((child) =>
      location.pathname.startsWith(child.path)
    );

    // Prevent closing if a child is active
    if (!open && isChildActive) return;

    setOpenSubMenus((prev) => ({ ...prev, [key]: open }));
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box
        sx={{
          height: 90,
          display: 'flex',
          alignItems: 'center',
          justifyContent: desktopOpen ? 'flex-start' : 'center',
          px: desktopOpen ? 3 : 0,
          transition: 'all 0.3s ease',
          marginTop: mobileOpen ? 8 : 0,
        }}
      >
        {desktopOpen ? (
          <Box
            component='img'
            src={companyLogo || logo}
            alt='Logo'
            sx={{ height: 75, pl: 4, objectFit: 'contain', mt: 1, mb: 1 }}
          />
        ) : (
          <Tooltip title='SVM BAKERY' placement='right'>
            <Box component='img' src={companyLogo || logo} alt='Logo' sx={{ height: 30, objectFit: 'contain' }} />
          </Tooltip>
        )}
      </Box>
      <Divider />

      {/* Menu Items */}
      <List sx={{ mt: 1, }}>
        {mainMenuItems.map((item) => {
          if (item.roles && !item.roles.includes(userRole)) return null;
          const isActive = item.path
            ? location.pathname.startsWith(item.path)
            : item.children?.some((child) => location.pathname.startsWith(child.path));
          const isOpen = openSubMenus[item.text] || false;

          if (item.children) {
            const parentProps = {
              sx: {
                justifyContent: desktopOpen || mobileOpen ? 'initial' : 'center',
                px: 1.5,
                py: 1.2,
                mx: 1,
                mb: 0.5,
                borderRadius: 1.5,
                backgroundColor: isActive ? '#f2e6fc' : 'transparent',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              },
            };

            const parentItem = (
              <ListItem
                button
                {...(!item.children || item.path ? { component: Link, to: item.path } : {})}
                onClick={() => {
                  if (!item.hover) toggleSubMenu(item.text, !isOpen);
                  if (mobileOpen && item.path) setMobileOpen(false);
                }}
                {...parentProps}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: desktopOpen || mobileOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: isActive ? Color.primary.main : '#333',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(desktopOpen || mobileOpen) && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '16px',
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? Color.primary.main : '#333',
                    }}
                  />
                )}
                {(desktopOpen || mobileOpen) && item.children && (
                  <Box
                    sx={{
                      ml: 1,
                      mr:'18px', // Adds space to the left of the arrow, matching mx: 1 of the menu item
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                )}
              </ListItem>
            );

            return (
              <Box
                key={item.text}
                onMouseEnter={() => item.hover && toggleSubMenu(item.text, true)}
                onMouseLeave={() => item.hover && toggleSubMenu(item.text, false)}
                sx={{ width: '100%' }}
              >
                {parentItem}

                {/* Submenu */}
                <Collapse in={isOpen} timeout={0} unmountOnExit>
                  <List component='div' disablePadding sx={{ width: '100%' }}>
                    {item.children.map((child) => {
                      const childActive = location.pathname.startsWith(child.path);
                      return (
                        <Tooltip
                          key={child.text}
                          title={desktopOpen || mobileOpen ? '' : child.text}
                          placement='right'
                        >
                          <ListItem
                            button
                            component={Link}
                            to={child.path}
                            onClick={() => {
                              setMobileOpen(false);
                            }}
                            sx={{
                              pl: desktopOpen ? 6 : 1,
                              mx: 1,
                              mb: 0.5,
                              borderRadius: 1.5,
                              backgroundColor: childActive ? '#f2e6fc' : 'transparent',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                              width: 'calc(100% - 8px)',
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: desktopOpen || mobileOpen ? 2 : 'auto',
                                justifyContent: 'center',
                                color: childActive ? Color.primary.main : '#333',
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            {(desktopOpen || mobileOpen) && (
                              <ListItemText
                                primary={child.text}
                                primaryTypographyProps={{
                                  fontSize: '14px',
                                  fontWeight: childActive ? 600 : 400,
                                  color: childActive ? Color.primary.main : '#333',
                                }}
                              />
                            )}
                          </ListItem>
                        </Tooltip>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          // Normal item
          return (
            <Tooltip key={item.text} title={desktopOpen || mobileOpen ? '' : item.text} placement='right'>
              <ListItem
                button
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                sx={{
                  justifyContent: desktopOpen || mobileOpen ? 'initial' : 'center',
                  px: 1.5,
                  py: 1.2,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1.5,
                  backgroundColor: isActive ? '#f2e6fc' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: desktopOpen || mobileOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: isActive ? Color.primary.main : '#333',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(desktopOpen || mobileOpen) && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '16px',
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? Color.primary.main : '#333',
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* Logout - Hidden in mobile view */}
      {!isMobile && (
        <List>
          <Tooltip title={desktopOpen ? '' : 'Logout'} placement='right'>
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                justifyContent: desktopOpen ? 'initial' : 'center',
                px: 1,
                py: 0.5,
                mx: 1,
                borderRadius: 2,
                background: '#D51721',
                minHeight: 32,
                '&:hover': { backgroundColor: '#D51721' },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: desktopOpen ? 2 : 'auto',
                  justifyContent: 'center',
                  color: '#ffffff',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {desktopOpen && (
                <ListItemText
                  primary='Logout'
                  primaryTypographyProps={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#fff',
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        </List>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100vw', overflowX: 'hidden' }}>
      <CssBaseline />
      <Header
        currentDrawerWidth={currentDrawerWidth}
        handleMobileToggle={handleMobileToggle}
        handleDesktopToggle={handleDesktopToggle}
        desktopOpen={desktopOpen}
      />

      <Box component='nav' sx={{ width: { sm: currentDrawerWidth }, flexShrink: 0 }}>
        {/* Mobile Drawer */}
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleMobileToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: fullDrawerWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: 'none !important',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant='permanent'
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: currentDrawerWidth,
              backgroundColor: '#ffffff',
              boxShadow: 'none',
              border: 'none',
              transition: 'width 0.3s ease',
              boxSizing: 'border-box',
              overflowX: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component='main'
        sx={{
          flexGrow: 1,
          mt: '50px',
          height: 'calc(100vh - 50px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
          transition: (theme) =>
            theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
          backgroundColor: '#F0F1F7',
          p: { xs: 2, sm: 4, md: 4 },
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Sidebar;